
























































































































import { ValidationProvider, ValidationObserver, extend } from "vee-validate";
import { email, numeric, max, min, regex, required } from "vee-validate/dist/rules";
import SfInputCustom from "~/components/atoms/SfInputCustom.vue";
import {
  defineComponent,
  ref,
  useContext,
  computed,
  onMounted,
} from "@nuxtjs/composition-api";
import userGetters from "~/modules/customer/getters/userGetters";
import { useUser } from "~/modules/customer/composables/useUser";
import { useUiNotification } from "~/composables";
import type { SubmitEventPayload } from "~/modules/customer/types/form";
import type { ProfileUpdateFormFields } from "~/modules/customer/pages/MyAccount/MyProfile/types";
import { useConfigStore } from "~/stores/extensionApi";
import { formatDateTime, isEmail } from "~/helpers";

extend("email", {
  ...email,
  message: "The email field must be a valid email",
});

extend("numeric", {
  ...numeric,
  message: "Số điện thoại không hợp lệ.",
});

extend("max", {
  ...max,
  message: "Số điện thoại không hợp lệ.",
});

extend("regex", {
  ...regex,
  validate(value) {
    const regex =
      /^[a-zA-ZÀÁÂÃÈÉÊÌÍÒÓÔÕÙÚĂĐĨŨƠàáâãèéêìíòóôõùúăđĩũơƯĂẠẢẤẦẨẪẬẮẰẲẴẶẸẺẼỀỀỂưăạảấầẩẫậắằẳẵặẹẻẽềềểếỄỆỈỊỌỎỐỒỔỖỘỚỜỞỠỢỤỦỨỪễệỉịọỏốồổỗộớờởỡợụủứừỬỮỰỲỴÝỶỸửữựỳỵỷỹ\s]+$/;
    return regex.test(value);
  },
  message: "Họ và tên không hợp lệ.",
});

extend("min", {
  ...min,
  message: "Số điện thoại không hợp lệ.",
});

extend("requiredDate", {
  ...required,
  message: "Ngày sinh không được để trống",
});

extend("age_limit", {
  validate(value) {
    if (!value) return true;

    const birthDate = new Date(value);
    const today = new Date();
    if (birthDate > today) {
      return false;
    }

    const currentYear = today.getFullYear();
    const birthYear = birthDate.getFullYear();
    const age = currentYear - birthYear;

    return age <= 100 && age >= 0 && !isNaN(age);
  },
  message: "Sinh nhật không hợp lệ, vui lòng nhập lại.",
});

export default defineComponent({
  components: {
    SfInputCustom,
    ValidationProvider,
    ValidationObserver,
  },
  props: {
    loading: {
      type: Boolean,
      required: false,
      default: false,
    },
  },
  emits: ["submit"],
  setup(_props, { emit }) {
    const storeConfig = useConfigStore();
    const otpConfig = computed(() => storeConfig.getOtpConfig);
    const defaultDob = computed(() => storeConfig.getOtpConfig?.default_dob);

    const {
      app: { i18n },
    } = useContext();
    const { user } = useUser();

    const dateOfBirth = computed(() => user.value.date_of_birth);

    const getInitialForm = (): ProfileUpdateFormFields => ({
      firstname: userGetters.getFirstName(user.value) && userGetters.getFirstName(user.value) !== "-" ? userGetters.getFirstName(user.value) : "",
      lastname: "-",
      email: isEmail(userGetters.getEmailAddress(user.value)) ? userGetters.getEmailAddress(user.value) : "",
      phone_number: userGetters.getPhoneLogin(user.value),
      date_of_birth: dateOfBirth.value != defaultDob.value ? userGetters.getDateOfBirth(user.value) : "",
      gender: userGetters.getGender(user.value),
    });
    const { send: sendNotification } = useUiNotification();

    const form = ref(getInitialForm());

    const submitForm = (resetValidationFn: () => void) => () => {
      const onComplete = () => {
        form.value = getInitialForm();
        sendNotification({
          id: Symbol("user_updated"),
          message: i18n.t(
            "The user account data was successfully updated!"
          ) as string,
          type: "success",
          icon: "check",
          persist: false,
          title: "User Account",
        });
        resetValidationFn();
      };

      const onError = (message: string) => {
        sendNotification({
          id: Symbol("user_updated"),
          message: i18n.t(message) as string,
          type: "danger",
          icon: "cross",
          persist: false,
          title: "User Account",
        });
      };

      const eventPayload: SubmitEventPayload<ProfileUpdateFormFields> = {
        form: form.value,
        onComplete,
        onError,
      };

      emit("submit", eventPayload);
    };

    const validateDob = (birthday: string): boolean => {
      const defaultDob = formatDateTime(otpConfig?.value?.default_dob, "DD/MM/YYYY");
      const timestampBirthday = formatDateTime(birthday, "DD/MM/YYYY")

      return defaultDob !== timestampBirthday;
    };

    const close = () => {
      emit("close");
    };

    const isFormValid = computed(() => {
      return (
        form.value.firstname?.trim() &&
        form.value.email?.trim() &&
        form.value.gender !== 0
      );
    });

    return {
      form,
      submitForm,
      validateDob,
      user,
      dateOfBirth,
      close,
      isFormValid,
    };
  },
});
